import React, { useState } from 'react'
import {
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Slider
} from '@mui/material'
import { useCreateHumidorMutation, useGetHumidorsQuery } from '../../store/api/humidorApi'
import { useAppSelector } from '../../store/hooks'

interface HumidorFormProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface FormData {
  name: string
  capacity: number
  location: string
  temperatureTarget: number | null
  humidityTarget: number | null
  notes: string
}

interface FormErrors {
  name?: string
  capacity?: string
  location?: string
  temperatureTarget?: string
  humidityTarget?: string
  notes?: string
}

const HumidorForm: React.FC<HumidorFormProps> = ({ open, onClose, onSuccess }) => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)
  const [createHumidor, { isLoading, error }] = useCreateHumidorMutation()
  const { data: existingHumidors = [] } = useGetHumidorsQuery(undefined, {
    skip: !isAuthenticated
  })

  const isPremium = user?.subscriptionTier === 'premium'
  const freeLimit = 1
  const currentHumidorCount = existingHumidors.length
  const canCreateMore = isPremium || currentHumidorCount < freeLimit

  const [formData, setFormData] = useState<FormData>({
    name: '',
    capacity: 50,
    location: '',
    temperatureTarget: null,
    humidityTarget: null,
    notes: ''
  })

  const [formErrors, setFormErrors] = useState<FormErrors>({})

  const validateForm = (): boolean => {
    const errors: FormErrors = {}
    
    // Name validation - check if empty after trimming
    if (!formData.name || !formData.name.trim()) {
      errors.name = 'Humidor name is required'
    } else if (formData.name.trim().length > 100) {
      errors.name = 'Name must be less than 100 characters'
    }

    // Capacity validation
    if (!formData.capacity || formData.capacity < 1) {
      errors.capacity = 'Capacity must be at least 1'
    } else if (formData.capacity > 10000) {
      errors.capacity = 'Capacity must be less than 10000'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value
    const parsedValue = field === 'capacity' ? (parseInt(value) || 0) : value
    
    setFormData(prev => ({
      ...prev,
      [field]: parsedValue
    }))

    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const handleSliderChange = (field: 'temperatureTarget' | 'humidityTarget') => (
    _event: Event,
    newValue: number | number[]
  ) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Clear field error when user adjusts slider
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!validateForm()) {
      return
    }

    // Check premium limits
    if (!canCreateMore) {
      return
    }

    try {
      const humidorData = {
        name: formData.name.trim(),
        capacity: formData.capacity,
        location: formData.location.trim() || undefined,
        temperatureTarget: formData.temperatureTarget || undefined,
        humidityTarget: formData.humidityTarget || undefined,
        notes: formData.notes.trim() || undefined
      }

      await createHumidor(humidorData).unwrap()
      
      // Reset form
      setFormData({
        name: '',
        capacity: 50,
        location: '',
        temperatureTarget: null,
        humidityTarget: null,
        notes: ''
      })
      setFormErrors({})

      onSuccess?.()
      onClose()
    } catch (error) {
      // Error is handled by the mutation hook
      console.error('Failed to create humidor:', error)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        name: '',
        capacity: 50,
        location: '',
        temperatureTarget: null,
        humidityTarget: null,
        notes: ''
      })
      setFormErrors({})
      onClose()
    }
  }

  if (!isAuthenticated) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Alert severity="error">
            Please log in to create humidors.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Humidor</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Humidor Name *"
                value={formData.name}
                onChange={handleInputChange('name')}
                error={!!formErrors.name}
                helperText={formErrors.name || ' '}
                disabled={isLoading}
                required
              />
            </Grid>

            {/* Capacity */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacity *"
                type="number"
                value={formData.capacity}
                onChange={handleInputChange('capacity')}
                error={!!formErrors.capacity}
                helperText={formErrors.capacity || 'Number of cigars this humidor can hold'}
                disabled={isLoading}
                inputProps={{ min: 1, max: 10000 }}
                required
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={handleInputChange('location')}
                error={!!formErrors.location}
                helperText={formErrors.location || 'e.g., Living Room, Office, Basement'}
                disabled={isLoading}
                placeholder="Optional"
              />
            </Grid>

            {/* Temperature Target */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Target Temperature: {formData.temperatureTarget}°F
              </Typography>
              <Slider
                value={formData.temperatureTarget ?? 65}
                onChange={handleSliderChange('temperatureTarget')}
                min={60}
                max={75}
                step={1}
                marks={[
                  { value: 60, label: '60°F' },
                  { value: 65, label: '65°F' },
                  { value: 68, label: '68°F' },
                  { value: 70, label: '70°F' },
                  { value: 75, label: '75°F' }
                ]}
                disabled={isLoading}
              />
              {formErrors.temperatureTarget && (
                <Typography variant="caption" color="error">
                  {formErrors.temperatureTarget}
                </Typography>
              )}
            </Grid>

            {/* Humidity Target */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Target Humidity: {formData.humidityTarget}%
              </Typography>
              <Slider
                value={formData.humidityTarget ?? 70}
                onChange={handleSliderChange('humidityTarget')}
                min={0}
                max={100}
                step={1}
                marks={[
                  { value: 60, label: '60%' },
                  { value: 70, label: '70%' },
                  { value: 80, label: '80%' }
                ]}
                disabled={isLoading}
              />
              {formErrors.humidityTarget && (
                <Typography variant="caption" color="error">
                  {formErrors.humidityTarget}
                </Typography>
              )}
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleInputChange('notes')}
                error={!!formErrors.notes}
                helperText={formErrors.notes || 'Optional notes about this humidor'}
                disabled={isLoading}
                placeholder="Optional"
              />
            </Grid>
          </Grid>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              Failed to create humidor. Please check your input and try again.
            </Alert>
          )}

          {/* Premium Limit Message */}
          {!canCreateMore && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {isPremium 
                ? 'You have reached your humidor limit.' 
                : `Free accounts can create up to ${freeLimit} humidor${freeLimit === 1 ? '' : 's'}. Upgrade to premium for unlimited humidors.`
              }
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !canCreateMore}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Creating...' : 'Create Humidor'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default HumidorForm
